import { axiosInstance } from "../../../api/axiosInstance.tsx";
import axios from 'axios';

export const fetchBidding = async (): Promise<any[]> => {
  try {
    const response = await axiosInstance.get("/bidding/view_bidding");
    return response.data.map((bidding) => ({
      id: bidding.id,
      boldText: bidding.biddingName,
      textColumn1: bidding.biddingAddress,
      textColumn2: bidding.biddingType,
      textColumn3: bidding.biddingContactNumber,
      textColumn4: bidding.biddingEmail,
      status: bidding.biddingStatus,
    }));
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "Gagal mengambil data bidding. Silakan coba lagi."
    );
  }
};

export interface biddingSearchParams {
  biddingName?: string;
  biddingAddress?: string;
  biddingContactNumber?: string;
  biddingEmail?: string;
  biddingType?: string;
  biddingTaxId?: string;
  biddingBankDetails?: string;
  biddingStatus?: string;
  limit?: number;
  offset?: number;
  keyword?: string;
  orderBy?: string;
  order?: string;
}

export interface RABDetailSearchParams {
  keyword?: string;
  rabId?: string;
  ahsId?: string;
  volume?: string;
  totalHarga?: string;
  limit?: number;
  offset?: number;
  orderBy?: string;
  order?: string;
}

export interface BiddingAdjustmentData {
  biddingId: string;
  rabId: string;
  rabDetailId: string;
  ahsId: string;
  koefisien: number;
  totalHarga: number;
  isCustom?: boolean;
}

export const getBidding = async (params: biddingSearchParams = {}) => {
  const response = await axiosInstance.get("/sales/bidding/search_bidding", {
    params,
  });
  return response.data;
};

// Step 1: Create Bidding
export const createBidding = async (data) => {
  // Pastikan vendorNames adalah array, bahkan jika hanya berisi 1 elemen
  const vendorNamesArray = Array.isArray(data.vendorNames) 
    ? data.vendorNames 
    : data.vendorNames 
      ? [data.vendorNames] 
      : [];

  const payload = {
    projectName: data.projectListingId,
    clientName: data.clientName,
    projectId: data.projectId,
    projectType: data.projectType,
    projectLocation: 
    "Address : " + data.projectAddress + 
    ", Long : " + data.longitude +
    ", Lat : " + data.latitude +
    ", Region : " + data.biddingRegion +
    ", City : " + data.biddingCity +
    ", District : " + data.biddingDistrict, 
    constructionPhase: data.constructionPhase,
    rabName: data.rabId,
    bidAmmount: data.bidAmount,
    submissionDate: data.submissionDate,
    projectAddress: data.projectAddress,
    rabValue: data.rabValue,
    startDate: data.startDate,
    endDate: data.endDate,
    projectProcess: data.projectProcess,
    vendorNames: vendorNamesArray, // Langsung kirim sebagai array
    vendorProjectValue: data.vendorProjectValue,
    status: data.status,
  };

  const response = await axiosInstance.post(`sales/bidding/create_bidding`, payload);
  return response.data;
};

// Create Office Location
export const createBiddingLocation = async (data) => {
  const response = await axiosInstance.post(
    `/biddingLocations/create_bidding_location`,
    data
  );
  return response.data; // contains the office `id` or `code`
};

// Step 2: Upload Photo by Item ID
export const uploadItemPhoto = async (id, file) => {
  const formData = new FormData();
  formData.append("photo", file);

  return axiosInstance.post(`/bidding/upload_photo/${id}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const fetchBiddingPhotoBlob = async (
  filename: string
): Promise<string | null> => {
  try {
    const token = localStorage.getItem("token");

    const response = await axiosInstance.get(`/bidding/photo/${filename}`, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    console.log(response.data);
    const objectUrl = URL.createObjectURL(response.data);
    return objectUrl;
  } catch (error) {
    console.error("Gagal mengambil foto:", error);
    return null;
  }
};

export const uploadProposal = async (id, file) => {
  const formData = new FormData();
  formData.append("proposal", file); // Changed from "photo" to "proposal"

  try {
    const response = await axiosInstance.post(
      `sales/bidding/upload_proposal/${id}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error uploading proposal:", error);
    throw error;
  }
};

export const getProposalFile = async (filename: string) => {
  try {
    const response = await axiosInstance.get(
      `sales/bidding/get_proposal/${encodeURIComponent(filename)}`,
      {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        }
      }
    );

    // Verify we received a blob
    if (!(response.data instanceof Blob)) {
      throw new Error('Invalid file format received');
    }

    return {
      blob: response.data,
      filename: getFilenameFromHeaders(response.headers) || filename,
      contentType: response.headers['content-type'] || 'application/octet-stream'
    };
    
  } catch (error) {
    console.error(`Error fetching proposal file ${filename}:`, error);
    
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error('Proposal file not found');
      }
      if (error.response?.status === 403) {
        throw new Error('Permission denied to access this file');
      }
    }
    
    throw new Error('Failed to download proposal file');
  }
};

// Helper function to extract filename from headers
const getFilenameFromHeaders = (headers) => {
  const contentDisposition = headers['content-disposition'];
  if (!contentDisposition) return null;
  
  const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
  if (matches && matches[1]) { 
    return matches[1].replace(/['"]/g, '');
  }
  return null;
};

// Delete bidding
export const deleteBidding = async (id: string) => {
  try {
    await axiosInstance.delete(`sales/bidding/delete_bidding/${id}`);
  } catch (error) {
    throw (
      error.response?.data?.message || "Terjadi kesalahan saat menghapus bidding"
    );
  }
};

export const deleteProposalFile = async (id: string) => {
  try {
    await axiosInstance.delete(`sales/bidding/delete_proposal/${id}`);
  } catch (error) {
    throw (
      error.response?.data?.message || "Terjadi kesalahan saat menghapus bidding location"
    );
  }
};

export const deleteBiddingLocation = async (id: number) => {
  try {
    await axiosInstance.delete(`/biddingLocations/delete_bidding_location/${id}`);
  } catch (error) {
    throw (
      error.response?.data?.message || "Terjadi kesalahan saat menghapus bidding location"
    );
  }
};

export const updateBidding = async (data) => {
  // Ensure vendorNames is always an array
  const vendorNamesArray = Array.isArray(data.vendorNames) 
    ? data.vendorNames 
    : data.vendorNames 
      ? [data.vendorNames] 
      : [];

  const payload = {
    projectName: data.projectListingId,
    clientName: data.clientName,
    projectId: data.projectId,
    projectType: data.projectType,
    projectLocation: 
      `Address: ${data.projectAddress}, ` +
      `Long: ${data.longitude}, ` +
      `Lat: ${data.latitude}, ` +
      `Region: ${data.biddingRegion}, ` +
      `City: ${data.biddingCity}, ` +
      `District: ${data.biddingDistrict}`,
    constructionPhase: data.constructionPhase,
    rabName: data.rabId,
    bidAmmount: data.bidAmount,
    submissionDate: data.submissionDate,
    projectAddress: data.projectAddress,
    rabValue: data.rabValue,
    startDate: data.startDate,
    endDate: data.endDate,
    projectProcess: data.projectProcess,
    vendorNames: vendorNamesArray,
    vendorProjectValue: data.vendorProjectValue,
    status: data.status,
    latitude: data.latitude,
    longitude: data.longitude,
    biddingRegion: data.biddingRegion,
    biddingCity: data.biddingCity,
    biddingDistrict: data.biddingDistrict
  };

  const response = await axiosInstance.put(
    `/sales/bidding/update_bidding/${data.id}`,
    payload
  );
  return response.data;
};

// Fetch Bidding Details by ID
export const getBiddingById = async (id) => {
  try {
    const response = await axiosInstance.get(`/bidding/view_bidding/${id}`);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data?.message ||
      "Terjadi kesalahan saat mengambil data bidding"
    );
  }
};

// Fungsi untuk mencari bidding berdasarkan keyword
export const sesarchBidding = async (searchQuery: string) => {
  try {
    const response = await axiosInstance.get(
      `/bidding/search_bidding?keyword=${searchQuery}`
    );
    return response.data; // Pastikan response sesuai format yang diterima tabel
  } catch (error) {
    console.error("Error fetching bidding:", error);
    throw (
      error.response?.data?.message ||
      "Terjadi kesalahan saat mengambil data karyawan"
    );
  }
};

export const fetchVendors = async (): Promise<any[]> => {
  try {
    const response = await axiosInstance.get("/vendors/view_vendors");
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "Gagal mengambil data vendor. Silakan coba lagi."
    );
  }
};

export const getRabDetailsByRabId = async (rabId: string) => {
  const response = await axiosInstance.get(`/sales/rab-detail/search_rab_detail?rabId=${rabId}&limit=1000000`);
  return response.data;
};

export const getRabDetail = async (params: RABDetailSearchParams = {}) => {
  const response = await axiosInstance.get("/sales/rab-detail/search_rab_detail", {
    params,
  });
  return response.data;
};

export const fetchAhs = async (): Promise<any[]> => {
  try {
    const response = await axiosInstance.get("/sales/ahs/view_ahs");
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "Gagal mengambil data ahs. Silakan coba lagi."
    );
  }
};

// Create Bidding Adjustment
export const createBiddingAdjustment = async (data: BiddingAdjustmentData) => {
  const response = await axiosInstance.post(
    "sales/bidding-adjustment/create_bidding_adjustment",
    data
  );
  return response.data;
};

// Create Multiple Bidding Adjustments (untuk looping)
export const createBiddingAdjustments = async (adjustments: BiddingAdjustmentData[]) => {
  const responses = await Promise.all(
    adjustments.map(adjustment => 
      axiosInstance.post(
        "/sales/bidding-adjustment/create_bidding_adjustment",
        adjustment
      )
    )
  );
  return responses.map(response => response.data);
};

// View Bidding Adjustments by RAB ID
export const viewBiddingAdjustment = async (): Promise<any> => {
  try {
    const response = await axiosInstance.get(
      `/sales/bidding-adjustment/view_bidding_adjustment`
    );
    
    // Handle berbagai format response
    if (Array.isArray(response.data)) {
      return response.data; // Jika response langsung array
    } else if (response.data.data && Array.isArray(response.data.data)) {
      return response.data.data; // Jika response { data: [...] }
    } else {
      return []; // Return empty array jika format tidak dikenali
    }
  } catch (error) {
    console.error('Error fetching bidding adjustments:', error);
    throw error;
  }
};

// Get Bidding Adjustments by RAB ID
export const getBiddingAdjustmentsByRabId = async (id: string) => {
  const response = await axiosInstance.get(
    `/sales/bidding-adjustment/view_bidding_adjustment/${id}`
  );
  return response.data;
};

// Get Bidding Adjustments by RAB Detail ID
export const getBiddingAdjustmentsByRabDetailId = async (id: string) => {
  const response = await axiosInstance.get(
    `/sales/bidding-adjustment/view_bidding_adjustment/rab_detail/${id}`
  );
  return response.data;
};

// Update Bidding Adjustment
export const updateBiddingAdjustment = async (id: string, data: Partial<BiddingAdjustmentData>) => {
  const response = await axiosInstance.put(
    `/sales/bidding-adjustment/update_bidding_adjustment/${id}`,
    data
  );
  return response.data;
};

// Delete Bidding Adjustment
export const deleteBiddingAdjustment = async (id: string) => {
  const response = await axiosInstance.delete(
    `/sales/bidding-adjustment/delete_bidding_adjustment/${id}`
  )
  return response.data;
};

// Get Bidding Adjustments by Bidding ID
export const getBiddingAdjustmentsByBiddingId = async (biddingId: string) => {
  try {
    const allAdjustments = await viewBiddingAdjustment();
    
    // Pastikan kita selalu mendapatkan array
    const adjustmentsArray = Array.isArray(allAdjustments) 
      ? allAdjustments 
      : allAdjustments.data || [];
    
    // Filter berdasarkan biddingId
    const filteredAdjustments = adjustmentsArray.filter(
      (adj) => adj.biddingId === biddingId
    );
    
    console.log(`Found ${filteredAdjustments.length} adjustments for bidding ${biddingId}`);
    return { data: filteredAdjustments };
  } catch (error) {
    console.error('Error fetching bidding adjustments by biddingId:', error);
    throw error;
  }
};